import axiosInstance from '@/lib/request.js';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

/**
 * @typedef {Object} CartItem
 * @property {string} id - Cart item UUID
 * @property {string} user_id - User ID who owns the cart
 * @property {string} product_id - Product ID
 * @property {string} [product_variant_id] - Product variant ID (for products with variants)
 * @property {number} quantity - Item quantity
 * @property {Object} [product_attributes] - Selected product attributes
 * @property {string} created_at - Creation timestamp
 * @property {string} updated_at - Last update timestamp
 * @property {string} product_name - Product name
 * @property {number} product_price - Product base price
 * @property {string} product_image - Product featured image
 * @property {string} product_sku - Product SKU
 * @property {number} product_stock_quantity - Product stock quantity
 * @property {string} product_stock_status - Product stock status
 * @property {boolean} has_variants - Whether product has variants
 * @property {string} [variant_id] - Variant ID
 * @property {string} [variant_sku] - Variant SKU
 * @property {string} [variant_title] - Variant title (e.g., "Blue/Large")
 * @property {number} [variant_price] - Variant price
 * @property {number} [variant_stock_quantity] - Variant stock quantity
 * @property {string} [variant_image] - Variant image
 * @property {boolean} [variant_is_active] - Whether variant is active
 * @property {Array} [variant_option_values] - Variant option values (color, size, etc.)
 */

/**
 * @typedef {Object} CartSummary
 * @property {number} total_items - Total quantity of items
 * @property {number} unique_items - Number of unique products/variants
 * @property {number} total_price - Total cart value
 */

/**
 * @typedef {Object} CartValidation
 * @property {boolean} is_valid - Whether cart is valid for checkout
 * @property {boolean} has_issues - Whether cart has any issues
 * @property {CartItem[]} valid_items - Items that are valid
 * @property {CartItem[]} invalid_items - Items with issues
 * @property {Object} summary - Validation summary
 * @property {Object} issue_categories - Issues categorized by type
 * @property {string[]} recommendations - Recommended actions
 */

/**
 * @typedef {Object} AddToCartData
 * @property {string} product_id - Product ID
 * @property {string} [product_variant_id] - Product variant ID (required for variant products)
 * @property {number} [quantity=1] - Quantity to add
 * @property {Object} [product_attributes] - Product attributes
 */

/**
 * @typedef {Object} UpdateCartItemData
 * @property {string} cart_item_id - Cart item ID
 * @property {number} quantity - New quantity (0 to remove)
 */

// Query Keys Factory - Centralized key management
export const cartKeys = {
  all: ['cart'],
  items: () => [...cartKeys.all, 'items'],
  summary: () => [...cartKeys.all, 'summary'],
  count: () => [...cartKeys.all, 'count'],
  validation: () => [...cartKeys.all, 'validation'],
  check: (productId, variantId) => [
    ...cartKeys.all,
    'check',
    productId,
    variantId,
  ],
};

// ===== QUERY HOOKS =====

/**
 * Hook to fetch user's cart items with full details
 * @param {Object} [options] - React Query options
 * @returns {import('@tanstack/react-query').UseQueryResult<{items: CartItem[], summary: CartSummary, total_items: number}, Error>}
 */
export const useCartItems = (options = {}) => {
  return useQuery({
    queryKey: cartKeys.items(),
    queryFn: async () => {
      const { data } = await axiosInstance.get('/shopping-cart');
      return data;
    },
    staleTime: 120 * 1000, // 2 minutes - cart data changes frequently
    ...options,
  });
};

/**
 * Hook to fetch cart summary (totals without full item details)
 * @param {Object} [options] - React Query options
 * @returns {import('@tanstack/react-query').UseQueryResult<CartSummary, Error>}
 */
export const useCartSummary = (options = {}) => {
  return useQuery({
    queryKey: cartKeys.summary(),
    queryFn: async () => {
      const { data } = await axiosInstance.get('/shopping-cart/summary');
      return data;
    },
    staleTime: 30 * 1000,
    ...options,
  });
};

/**
 * Hook to fetch cart item count (for badge display)
 * @param {Object} [options] - React Query options
 * @returns {import('@tanstack/react-query').UseQueryResult<{item_count: number}, Error>}
 */
export const useCartItemCount = (options = {}) => {
  return useQuery({
    queryKey: cartKeys.count(),
    queryFn: async () => {
      const { data } = await axiosInstance.get('/shopping-cart/count');
      return data;
    },
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000, // Refetch every minute for real-time badge updates
    ...options,
  });
};

/**
 * Hook to validate cart items (stock, availability)
 * @param {Object} [options] - React Query options
 * @returns {import('@tanstack/react-query').UseQueryResult<CartValidation, Error>}
 */
export const useCartValidation = (options = {}) => {
  return useQuery({
    queryKey: cartKeys.validation(),
    queryFn: async () => {
      const { data } = await axiosInstance.get('/shopping-cart/validate');
      return data;
    },
    staleTime: 10 * 1000, // 10 seconds - validation data is time-sensitive
    ...options,
  });
};

/**
 * Hook to check if specific product/variant exists in cart
 * @param {string} productId - Product ID
 * @param {string} [variantId] - Product variant ID
 * @param {Object} [options] - React Query options
 * @returns {import('@tanstack/react-query').UseQueryResult<{exists: boolean, cart_item: CartItem|null}, Error>}
 */
export const useCartItemCheck = (productId, variantId = null, options = {}) => {
  return useQuery({
    queryKey: cartKeys.check(productId, variantId),
    queryFn: async () => {
      const params = { product_id: productId };
      if (variantId) {
        params.product_variant_id = variantId;
      }

      const { data } = await axiosInstance.get('/shopping-cart/check', {
        params,
      });
      return data;
    },
    enabled: !!productId,
    staleTime: 30 * 1000,
    ...options,
  });
};

// ===== MUTATION HOOKS =====

/**
 * Hook to add item to cart
 * @param {Object} [options] - React Query mutation options
 * @returns {import('@tanstack/react-query').UseMutationResult<{cart_item: CartItem, cart_summary: CartSummary}, Error, AddToCartData>}
 */
export const useAddToCart = (options = {}) => {
  const queryClient = useQueryClient();

  const {
    onSuccess: componentOnSuccess,
    onError: componentOnError,
    ...otherOptions
  } = options;

  return useMutation({
    mutationFn: async (cartData) => {
      const { data } = await axiosInstance.post('/shopping-cart', cartData);
      return data;
    },
    onSuccess: (result, variables) => {
      // Update cart queries
      queryClient.invalidateQueries({ queryKey: cartKeys.items() });
      queryClient.invalidateQueries({ queryKey: cartKeys.count() });
      queryClient.invalidateQueries({ queryKey: cartKeys.validation() });

      // Update summary cache directly if available
      if (result.cart_summary) {
        queryClient.setQueryData(cartKeys.summary(), result.cart_summary);
      }

      // Update the check query for this specific product/variant
      queryClient.setQueryData(
        cartKeys.check(variables.product_id, variables.product_variant_id),
        {
          exists: true,
          cart_item: result.cart_item,
          product_id: variables.product_id,
          product_variant_id: variables.product_variant_id || null,
        }
      );
      if (componentOnSuccess) {
        componentOnSuccess(result, variables);
      }
    },
    onError: (error, variables, context) => {
      if (componentOnError) {
        componentOnError(error, variables, context);
      }
    },
    ...otherOptions,
  });
};

/**
 * Hook to update cart item quantity
 * @param {Object} [options] - React Query mutation options
 * @returns {import('@tanstack/react-query').UseMutationResult<{cart_item?: CartItem, cart_summary: CartSummary, removed_item_id?: string}, Error, UpdateCartItemData>}
 */
export const useUpdateCartItem = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ cart_item_id, quantity }) => {
      const { data } = await axiosInstance.put(
        `/shopping-cart/${cart_item_id}`,
        {
          quantity,
        }
      );
      return data;
    },
    onSuccess: (result) => {
      // Invalidate cart queries
      queryClient.invalidateQueries({ queryKey: cartKeys.items() });
      queryClient.invalidateQueries({ queryKey: cartKeys.count() });
      queryClient.invalidateQueries({ queryKey: cartKeys.validation() });

      // Update summary cache directly
      if (result.cart_summary) {
        queryClient.setQueryData(cartKeys.summary(), result.cart_summary);
      }

      // If item was removed, invalidate check queries
      if (result.removed_item_id) {
        queryClient.invalidateQueries({ queryKey: cartKeys.check() });
      }
    },
    ...options,
  });
};

/**
 * Hook to remove item from cart
 * @param {Object} [options] - React Query mutation options
 * @returns {import('@tanstack/react-query').UseMutationResult<{cart_summary: CartSummary, removed_item_id: string}, Error, string>}
 */
export const useRemoveFromCart = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (cartItemId) => {
      const { data } = await axiosInstance.delete(
        `/shopping-cart/${cartItemId}`
      );
      return data;
    },
    onSuccess: (result) => {
      // Invalidate cart queries
      queryClient.invalidateQueries({ queryKey: cartKeys.items() });
      queryClient.invalidateQueries({ queryKey: cartKeys.count() });
      queryClient.invalidateQueries({ queryKey: cartKeys.validation() });
      queryClient.invalidateQueries({ queryKey: cartKeys.check() });

      // Update summary cache directly
      if (result.cart_summary) {
        queryClient.setQueryData(cartKeys.summary(), result.cart_summary);
      }
    },
    ...options,
  });
};

/**
 * Hook to clear entire cart
 * @param {Object} [options] - React Query mutation options
 * @returns {import('@tanstack/react-query').UseMutationResult<{items_removed: number, cart_summary: CartSummary}, Error, void>}
 */
export const useClearCart = (options = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      const { data } = await axiosInstance.delete('/shopping-cart');
      return data;
    },
    onSuccess: (result) => {
      // Clear all cart queries
      queryClient.removeQueries({ queryKey: cartKeys.items() });
      queryClient.removeQueries({ queryKey: cartKeys.validation() });
      queryClient.removeQueries({ queryKey: cartKeys.check() });

      // Set empty state for summary and count
      queryClient.setQueryData(cartKeys.summary(), {
        total_items: 0,
        unique_items: 0,
        total_price: 0,
      });

      queryClient.setQueryData(cartKeys.count(), { item_count: 0 });
    },
    ...options,
  });
};

// ===== UTILITY HOOKS =====

/**
 * Combined hook that provides all cart data and common operations
 * @param {Object} [options] - Options for individual queries
 * @returns {Object} Cart data and operations
 */
export const useShoppingCart = (options = {}) => {
  const queryClient = useQueryClient();

  // Queries
  const cartItemsQuery = useCartItems(options.items);
  const cartSummaryQuery = useCartSummary(options.summary);
  const cartCountQuery = useCartItemCount(options.count);

  // Mutations
  const addToCartMutation = useAddToCart(options.addToCart);
  const updateCartItemMutation = useUpdateCartItem(options.updateItem);
  const removeFromCartMutation = useRemoveFromCart(options.removeItem);
  const clearCartMutation = useClearCart(options.clearCart);

  // Derived state
  const isLoading =
    cartItemsQuery.isLoading ||
    cartSummaryQuery.isLoading ||
    cartCountQuery.isLoading;
  const isError =
    cartItemsQuery.isError ||
    cartSummaryQuery.isError ||
    cartCountQuery.isError;
  const isEmpty = cartCountQuery.data?.item_count === 0;

  // Helper functions
  const refreshCart = () => {
    queryClient.invalidateQueries({ queryKey: cartKeys.all });
  };

  const addToCart = (
    productId,
    variantId = null,
    quantity = 1,
    attributes = {}
  ) => {
    return addToCartMutation.mutateAsync({
      product_id: productId,
      product_variant_id: variantId,
      quantity,
      product_attributes: attributes,
    });
  };

  const updateItemQuantity = (cartItemId, quantity) => {
    return updateCartItemMutation.mutateAsync({
      cart_item_id: cartItemId,
      quantity,
    });
  };

  const removeItem = (cartItemId) => {
    return removeFromCartMutation.mutateAsync(cartItemId);
  };

  const clearCart = () => {
    return clearCartMutation.mutateAsync();
  };

  const getItemQuantity = (productId, variantId = null) => {
    const items = cartItemsQuery.data?.items || [];
    const item = items.find(
      (item) =>
        item.product_id === productId &&
        (variantId
          ? item.product_variant_id === variantId
          : !item.product_variant_id)
    );
    return item?.quantity || 0;
  };

  const isItemInCart = (productId, variantId = null) => {
    return getItemQuantity(productId, variantId) > 0;
  };

  return {
    // Data
    items: cartItemsQuery.data?.items || [],
    summary: cartSummaryQuery.data || {
      total_items: 0,
      unique_items: 0,
      total_price: 0,
    },
    itemCount: cartCountQuery.data?.item_count || 0,

    // State
    isLoading,
    isError,
    isEmpty,

    // Individual query states
    itemsQuery: cartItemsQuery,
    summaryQuery: cartSummaryQuery,
    countQuery: cartCountQuery,

    // Mutations
    addToCartMutation,
    updateCartItemMutation,
    removeFromCartMutation,
    clearCartMutation,

    // Operations
    addToCart,
    updateItemQuantity,
    removeItem,
    clearCart,
    refreshCart,

    // Helpers
    getItemQuantity,
    isItemInCart,

    // Mutation states
    isAdding: addToCartMutation.isPending,
    isUpdating: updateCartItemMutation.isPending,
    isRemoving: removeFromCartMutation.isPending,
    isClearing: clearCartMutation.isPending,

    // Error states
    addError: addToCartMutation.error,
    updateError: updateCartItemMutation.error,
    removeError: removeFromCartMutation.error,
    clearError: clearCartMutation.error,
  };
};

// ===== SPECIALIZED HOOKS =====

/**
 * Hook for cart validation with automatic refresh
 * @param {boolean} [enabled=false] - Whether to enable automatic validation
 * @param {number} [interval=30000] - Validation interval in milliseconds
 * @returns {import('@tanstack/react-query').UseQueryResult<CartValidation, Error>}
 */
export const useCartValidationWithInterval = (
  enabled = false,
  interval = 30000
) => {
  return useCartValidation({
    enabled,
    refetchInterval: enabled ? interval : false,
    refetchIntervalInBackground: false,
  });
};

/**
 * Hook to check multiple products/variants at once
 * @param {Array<{productId: string, variantId?: string}>} items - Items to check
 * @param {Object} [options] - React Query options
 * @returns {Array} Array of check results
 */
export const useMultipleCartItemChecks = (items = [], options = {}) => {
  return items.map(({ productId, variantId }) =>
    useCartItemCheck(productId, variantId, options)
  );
};
